<script setup lang="ts">
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import { INDEX } from '~/constants/routes/routes.constants'
import type { SideBarItemsType } from '~/schema/sidebar.schema'
import { ARTICLE_CREATE } from '~/constants/routes/article/routes.constants'
import {
  QUICK_ACCESS_ARTICLE_ADAY,
  QUICK_ACCESS_ARTICLE_CFC,
  QUICK_ACCESS_ARTICLE_WITHOUT_CFC
} from '~/constants/routes/quick-access/routes.constants'
import { SETTINGS_CUSTOMER } from '~/constants/routes/settings/customer/routes.constants'
import { SETTINGS_SOURCE } from '~/constants/routes/settings/source/routes.constants'
import { SETTINGS_FTP } from '~/constants/routes/settings/ftp/routes.constants'
import { SETTINGS_USER } from '~/constants/routes/settings/user/routes.constants'
import { SETTINGS_MYRDP_USER } from '~/constants/routes/settings/myrdp/routes.constants'
import { SETTINGS_AUTHOR } from '~/constants/routes/settings/author/routes.constants'
import {
  QUICK_ACCESS_PRESS_REVIEW,
  QUICK_ACCESS_PRESS_REVIEW_CUSTOMER_VIEW
} from '~/constants/routes/quick-access/pressReview/routes.constants'
import { ARCHIVE_ARTICLE_ARCHIVED, ARCHIVE_PRESS_REVIEW_ARCHIVED } from '~/constants/routes/archive/routes.constants'
import { SETTINGS_LOGS, SETTINGS_STATS } from '~/constants/routes/settings/routes.constants'
import { SETTINGS_FINANCIAL_ENTITY } from '~/constants/routes/settings/financial-entity/routes.constants'
import { SETTINGS_FINANCIAL_INDEX } from '~/constants/routes/settings/financial-index/routes.constants'
import { ARCHIVE_PRESS_REVIEW_ARCHIVED_CUSTOMER } from '~/constants/routes/archive/press-review-archived/routes.constants'
import { getCustomers, getCustomersActiveOnly } from '~/query/customer/customer.query'

import createArticle from '~/assets/icons/sidebar/quickAccess/create_article.svg'
import folderSpecial from '~/assets/icons/sidebar/quickAccess/folder_special.svg'
import folderZip from '~/assets/icons/sidebar/quickAccess/folder_zip.svg'
import quickAccessInventory from '~/assets/icons/sidebar/quickAccess/inventory.svg'

import folderOpen from '~/assets/icons/sidebar/archive/folder_open.svg'
import archiveInventory from '~/assets/icons/sidebar/archive/inventory.svg'

import user from '~/assets/icons/sidebar/settings/user.svg'
import apartment from '~/assets/icons/sidebar/settings/apartment.svg'
import libraryBooks from '~/assets/icons/sidebar/settings/library_books.svg'
import monitoring from '~/assets/icons/sidebar/settings/monitoring.svg'
import currencyExchange from '~/assets/icons/sidebar/settings/currency_exchange.svg'
import dns from '~/assets/icons/sidebar/settings/dns.svg'
import editDocument from '~/assets/icons/sidebar/settings/edit_document.svg'
import listAlt from '~/assets/icons/sidebar/settings/list_alt.svg'
import stockIndex from '~/assets/icons/sidebar/settings/stock-index.svg'

const props = defineProps<{
    sidebarOpen: boolean,
    sidebarOpenForDesktop: boolean
}>()

const emit = defineEmits<{
    (e: 'update:sidebarOpen', value: boolean): void,
    (e: 'update:sidebarOpenForDesktop', value: boolean): void
}>()

const sidebarOpen = computed({
  get () {
    return props.sidebarOpen
  },
  set (value) {
    emit('update:sidebarOpen', value)
  }
})

const sidebarOpenForDesktop = computed({
  get () {
    return props.sidebarOpenForDesktop
  },
  set (value) {
    emit('update:sidebarOpenForDesktop', value)
  }
})

const isAdmin = useAuth().isAdmin

const page = ref<number>(1)
const limit = ref<number>(9999)

const route = useRoute()

const currentPath = computed(() => route.path)
const routeParamsId = computed(() => route.params.id)

const { data, isSuccess } = getCustomersActiveOnly(page, limit, undefined, undefined)

const isInViewPressReviewArchived = computed(() => {
  const regex = /^\/archive\/press-review-archived\/?.*$/
  return regex.test(currentPath.value)
})

const generateChildrenPressReview = (pressReviewArchived: boolean = false) => {
  return data.value?.customers.map((item) => {
    const path = !pressReviewArchived ? QUICK_ACCESS_PRESS_REVIEW_CUSTOMER_VIEW.path.replace('{id}', item.id.toString()) : ARCHIVE_PRESS_REVIEW_ARCHIVED_CUSTOMER.path.replace('{id}', item.id.toString())

    const urlWithHostname = `${window.location.origin}${path}`
    const url = new URL(urlWithHostname)

    url.searchParams.append('go_to_first', '1')

    const urlWithoutHostname = url.href.replace(url.origin, '')

    return {
      anchor: true,
      anchorKey: pressReviewArchived ? 'nav-press-review-customer-archived-' : 'nav-press-review-customer-',
      anchorValue: item.id.toString(),
      id: item.id.toString(),
      title: item.name,
      route: urlWithoutHostname,
      ...(routeParamsId.value ? { active: item.id.toString() === routeParamsId.value.toString() } : {})
    }
  })
}

const childrenPressReview = computed(() => generateChildrenPressReview())
const childrenPressReviewArchived = computed(() => generateChildrenPressReview(true))

const quickAccess = computed<SideBarItemsType>(() => [
  {
    title: 'Articles Flux CFC',
    route: QUICK_ACCESS_ARTICLE_CFC,
    svg: folderSpecial
  },
  {
    title: 'Articles à traiter hors CFC',
    route: QUICK_ACCESS_ARTICLE_WITHOUT_CFC,
    svg: folderZip
  },
  {
    title: 'Articles Flux',
    route: QUICK_ACCESS_ARTICLE_ADAY,
    svg: folderZip
  },
  {
    title: 'Créer un article',
    route: ARTICLE_CREATE,
    svg: createArticle
  },
  {
    title: 'Revues de presse',
    route: QUICK_ACCESS_PRESS_REVIEW,
    svg: quickAccessInventory,
    defaultOpen: true,
    ...(isSuccess.value && childrenPressReview.value
      ? {
          children: [
            {
              title: 'Toutes les revues de presse',
              route: QUICK_ACCESS_PRESS_REVIEW.path
            },
            ...childrenPressReview.value
          ]
        }
      : {})
  }
])

const archive = computed<SideBarItemsType>(() => [
  { title: 'Articles archivés', route: ARCHIVE_ARTICLE_ARCHIVED, svg: folderOpen },
  {
    title: 'Revues de presse archivées',
    route: ARCHIVE_PRESS_REVIEW_ARCHIVED,
    svg: archiveInventory,
    defaultOpen: true,
    ...(isSuccess.value && isInViewPressReviewArchived.value && childrenPressReviewArchived.value
      ? {
          children: [
            {
              title: 'Toutes les revues de presse archivées',
              route: ARCHIVE_PRESS_REVIEW_ARCHIVED.path
            },
            ...childrenPressReviewArchived.value
          ]
        }
      : {})
  }
])

const settings = computed<SideBarItemsType>(() => [
  { title: 'Utilisateurs S&M', route: SETTINGS_USER, svg: user, show: isAdmin.value },
  { title: 'Utilisateurs MyRDP', route: SETTINGS_MYRDP_USER, svg: user, show: isAdmin.value },
  { title: 'Clients', route: SETTINGS_CUSTOMER, svg: apartment },
  { title: 'Sources', route: SETTINGS_SOURCE, svg: libraryBooks },
  { title: 'Auteurs', route: SETTINGS_AUTHOR, svg: editDocument },
  { title: 'Statistiques', route: SETTINGS_STATS, svg: monitoring },
  { title: 'Indicateurs financiers', route: SETTINGS_FINANCIAL_ENTITY, svg: currencyExchange },
  { title: 'Indice boursier', route: SETTINGS_FINANCIAL_INDEX, svg: stockIndex },
  { title: 'Paramètres FTP', route: SETTINGS_FTP, svg: dns },
  { title: "Journaux d'activité", route: SETTINGS_LOGS, svg: listAlt },
  { title: 'Comparatif Innosys', route: '/settings/innosys-compare', svg: listAlt }
])

watch(currentPath, () => {
  emit('update:sidebarOpen', false)
})
</script>

<template>
  <div v-if="sidebarOpenForDesktop">
    <TransitionRoot as="template" :show="sidebarOpen">
      <Dialog as="div" class="relative z-50 xl:hidden" @close="sidebarOpen = false">
        <TransitionChild
          as="template"
          enter="transition-opacity ease-linear duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="transition-opacity ease-linear duration-300"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div class="fixed inset-0 bg-gray-900/80" />
        </TransitionChild>

        <div class="fixed inset-0 flex">
          <TransitionChild
            as="template"
            enter="transition ease-in-out duration-300 transform"
            enter-from="-translate-x-full"
            enter-to="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leave-from="translate-x-0"
            leave-to="-translate-x-full"
          >
            <DialogPanel class="relative mr-16 flex w-full max-w-xs flex-1">
              <TransitionChild
                as="template"
                enter="ease-in-out duration-300"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="ease-in-out duration-300"
                leave-from="opacity-100"
                leave-to="opacity-0"
              >
                <div class="absolute left-full top-0 flex w-16 justify-center pt-5">
                  <button type="button" class="-m-2.5 p-2.5" @click="sidebarOpen = false">
                    <span class="sr-only">Close sidebar</span>
                    <XMarkIcon class="h-6 w-6 text-white" aria-hidden="true" />
                  </button>
                </div>
              </TransitionChild>
              <div class="sidebar flex grow flex-col gap-y-2 overflow-y-auto bg-white">
                <div class="py-1">
                  <NuxtLink
                    class="flex-none text-xl font-semibold dark:text-white"
                    :href="INDEX.path"
                    aria-label="Synthèse & Médias"
                  >
                    <img src="~/assets/images/logo/sm_logo.png" alt="Synthese Et Medias">
                  </NuxtLink>
                </div>
                <nav class="flex flex-1 flex-col bg-sidebar pt-5">
                  <ul role="list" class="flex flex-1 flex-col gap-y-7 pb-12 text-white">
                    <li>
                      <SideBarItem title="Accès rapide" :items="quickAccess" />
                      <SideBarItem class="mt-4" title="Archives" :items="archive" />
                      <SideBarItem class="mt-4" title="Paramètres" :items="settings" />
                    </li>
                  </ul>
                </nav>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </TransitionRoot>

    <div class="hidden xl:fixed xl:inset-y-0 xl:z-50 xl:flex xl:w-64 xl:flex-col bg-sidebar">
      <div class="sidebar flex grow flex-col gap-y-5 overflow-y-auto border-r">
        <div class="bg-white py-2.5">
          <NuxtLink
            class="flex-none text-xl font-semibold dark:text-white"
            :href="INDEX.path"
            aria-label="Synthèse & Médias"
          >
            <img src="~/assets/images/logo/sm_logo.png" alt="Synthese Et Medias">
          </NuxtLink>
        </div>
        <nav class="flex flex-1 flex-col text-white">
          <ul role="list" class="flex flex-1 flex-col gap-y-7 pb-12">
            <li>
              <SideBarItem title="Accès rapide" :items="quickAccess" />
              <SideBarItem class="mt-4" title="Archives" :items="archive" />
              <SideBarItem class="mt-4" title="Paramètres" :items="settings" />
            </li>
          </ul>
        </nav>
      </div>
    </div>
  </div>
</template>
